import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

// Firebase Config
import { db, auth } from '../../../../../firebase/config';
import { 
    collection, 
    query, 
    getDocs, 
    doc, 
    setDoc,
    collectionGroup, 
    serverTimestamp,
    deleteDoc,
    where,
    limit,
    writeBatch
} from 'firebase/firestore';

// Importación de la utilidad de borrado profundo
import { deleteRemesaDeudaArtFull } from '../../../../../firebase/firebaseUtils';

// Componentes de interfaz compartidos
import ModalConfirmacion from '../../../../ui/ModalConfirmacion'; 

/* STREAMING_CHUNK: Definiendo helpers de limpieza de monedas y segmentación de lotes */
/**
 * parseCurrency - Limpia strings de moneda y los convierte a float de forma segura
 */
const parseCurrency = (val) => {
    if (val === undefined || val === null || val === '') return 0;
    if (typeof val === 'number') return val;
    
    // Quita signos $, espacios y limpia separadores de miles/decimales al estilo ES/AR
    let clean = String(val).trim().replace('$', '').replace(/\s/g, '');
    if (clean.includes('.') && clean.includes(',')) {
        // Caso: 1.034.000,50 o 1,034,000.50
        clean = clean.replace(/\./g, '').replace(',', '.');
    } else if (clean.includes(',')) {
        // Caso: 1034000,50
        clean = clean.replace(',', '.');
    }
    const num = parseFloat(clean);
    return isNaN(num) ? 0 : num;
};

// Helper para dividir un array en grupos de tamaño N (Límites de Firestore 'in')
const chunkArray = (array, size) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
    }
    return chunks;
};

/* STREAMING_CHUNK: Inicializando estados del componente e importador */
/**
 * ImportarDeudasArtPage - Importador masivo de deudas activas de ART con matching de contrato,
 * cálculo secuencial de cuenta corriente en tiempo real y rollback transaccional automatizado.
 */
const ImportarDeudasArtPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('importar');
    const [file, setFile] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState({ success: 0, errors: 0 });
    const fileInputRef = useRef(null);

    // Caché de contratos y diccionario de movimientos previos
    const [contratosList, setContratosList] = useState([]);
    const [prevMovementsMap, setPrevMovementsMap] = useState({}); // { contratoId: true/false }
    const [loadingCaches, setLoadingCaches] = useState(false);

    // Estados para la vista previa interactiva antes de importar
    const [previewRows, setPreviewRows] = useState([]);
    const [showPreview, setShowPreview] = useState(false);

    // Estado de Remesas de Deuda (Ruta corregida: public/data/remesas_deuda_art)
    const [remesas, setRemesas] = useState([]);
    const [remesaToDelete, setRemesaToDelete] = useState(null);
    const [loadingRemesas, setLoadingRemesas] = useState(false);

    // Notificaciones de feedback para el usuario
    const [notification, setNotification] = useState({ show: false, title: '', message: '', type: 'success' });

    // Paginación del Historial
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const addLog = (msg, type = 'info') => {
        setLogs(prev => [...prev, { msg, type, time: new Date().toLocaleTimeString() }]);
    };

    /* STREAMING_CHUNK: Consultando contratos y escaneando auditorías previas */
    /**
     * fetchContratosAndCheckPrevMovements - Busca los contratos solicitados y consulta
     * en paralelo si poseen un libro diario previo en su subcolección 'historial_cobranza'
     */
    const fetchContratosAndCheckPrevMovements = async (contratosANecesitar) => {
        if (contratosANecesitar.length === 0) return [];
        setLoadingCaches(true);
        addLog(`[SISTEMA] Sincronizando diccionario de ${contratosANecesitar.length} contratos únicos de ART...`, "warning");
        
        try {
            const chunks = chunkArray(contratosANecesitar, 30);
            const promises = chunks.map(async (chunk) => {
                const qContratos = query(
                    collectionGroup(db, 'contratos'),
                    where('nContrato', 'in', chunk)
                );
                const snapContratos = await getDocs(qContratos);
                return snapContratos.docs.map(d => {
                    const parts = d.ref.path.split('/');
                    return {
                        id: d.id,
                        path: d.ref.path,
                        clienteId: parts[1],
                        contratoId: parts[3],
                        ...d.data()
                    };
                });
            });

            const results = await Promise.all(promises);
            const mappedContratos = results.flat();

            addLog(`[SISTEMA] Contratos ubicados. Iniciando escaneo de auditoría contable en paralelo...`, "info");

            // Analizamos en paralelo si poseen deudas o movimientos históricos
            const checkPromises = mappedContratos.map(async (c) => {
                const subRef = collection(db, `public/data/clientes/${c.clienteId}/contratos/${c.id}/historial_cobranza`);
                const qLimit = query(subRef, limit(1));
                const snapSub = await getDocs(qLimit);
                return { contratoId: c.id, hasHistory: !snapSub.empty };
            });

            const checkResults = await Promise.all(checkPromises);
            const historyMap = {};
            checkResults.forEach(res => {
                historyMap[res.contratoId] = res.hasHistory;
            });

            setContratosList(mappedContratos);
            setPrevMovementsMap(historyMap);
            
            addLog(`[SISTEMA] Auditoría finalizada. Caché lista para matching inmediato.`, "success");
            return { mappedContratos, historyMap };
        } catch (error) {
            console.error("Error cargando cachés de deudas ART:", error);
            addLog("Error crítico de sincronización contable con Firestore.", "error");
            return { mappedContratos: [], historyMap: {} };
        } finally {
            setLoadingCaches(false);
        }
    };

    /* STREAMING_CHUNK: Cargando historial de lotes e indexaciones de remesas */
    /**
     * fetchRemesas - Carga las remesas de deudas procesadas en los últimos 3 meses (Ruta corregida)
     */
    const fetchRemesas = async () => {
        setLoadingRemesas(true);
        try {
            const snap = await getDocs(collection(db, 'public/data/remesas_deuda_art'));
            
            const limiteMeses = new Date();
            limiteMeses.setMonth(limiteMeses.getMonth() - 3);
            const limitTime = limiteMeses.getTime();

            const dataFiltrada = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(r => {
                    const remesaDate = r.fecha ? new Date(r.fecha).getTime() : 0;
                    return remesaDate >= limitTime;
                });

            dataFiltrada.sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
            
            setRemesas(dataFiltrada);
            setCurrentPage(1); 
        } catch (error) {
            console.error("Error al cargar remesas de deudas:", error);
            addLog("Error al cargar remesas de deudas de la base de datos.", "error");
        } finally {
            setLoadingRemesas(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'remesas') {
            fetchRemesas();
        }
    }, [activeTab]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setProcessing(false);
        setLogs([]);
        setStats({ success: 0, errors: 0 });
        setPreviewRows([]);
        setShowPreview(false);
        setFile(selectedFile);
        setContratosList([]); 
        setPrevMovementsMap({});
    };

    const handleCancelPreview = () => {
        setFile(null);
        setPreviewRows([]);
        setShowPreview(false);
        setLogs([]);
        setStats({ success: 0, errors: 0 });
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    /* STREAMING_CHUNK: Procesando y proyectando el Excel entrante con diferencias de saldos */
    /**
     * parseExcelToPreview - Parsea el excel y proyecta la vista previa aplicando las reglas de negocio
     */
    const parseExcelToPreview = async () => {
        if (!file) return;
        setProcessing(true);
        setLogs([]);
        setStats({ success: 0, errors: 0 });

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = e.target.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                const json = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);

                if (json.length === 0) {
                    addLog("El archivo Excel seleccionado no contiene registros.", "error");
                    setProcessing(false);
                    return;
                }

                // Normalización de contratos únicos
                const contratosEnExcel = Array.from(new Set(
                    json.map(row => {
                        let nContratoExcel = row.nContrato || row.Contrato || row['N° Contrato'] || row['Contrato N°'] || '';
                        nContratoExcel = String(nContratoExcel).trim();
                        if (nContratoExcel.endsWith('.0')) nContratoExcel = nContratoExcel.replace('.0', '');
                        return nContratoExcel;
                    }).filter(Boolean)
                ));

                // Sincronización inteligente de base de datos
                const { mappedContratos, historyMap } = await fetchContratosAndCheckPrevMovements(contratosEnExcel);

                if (mappedContratos.length === 0) {
                    addLog("Advertencia: Ningún contrato coincide con deudores activos en base de datos.", "warning");
                }

                addLog(`Indexando ${json.length} filas del archivo de deudas...`, 'info');

                // Mapear contratos encontrados en memoria O(1)
                const contratosMap = {};
                mappedContratos.forEach(c => {
                    if (c.nContrato) {
                        contratosMap[String(c.nContrato).trim()] = c;
                    }
                });

                let validos = 0;
                let erroneos = 0;
                const rowsProcessed = [];

                json.forEach((row) => {
                    let nContratoExcel = row.nContrato || row.Contrato || row['N° Contrato'] || row['Contrato N°'] || '';
                    nContratoExcel = String(nContratoExcel).trim();
                    if (nContratoExcel.endsWith('.0')) nContratoExcel = nContratoExcel.replace('.0', '');

                    const totalDeudaExcel = parseCurrency(row.importe || row.Importe || row.monto || row.Monto || row.Deuda || row.deuda || 0);

                    if (!nContratoExcel) {
                        erroneos++;
                        return;
                    }

                    const contratoPadre = contratosMap[nContratoExcel];
                    const hasMatch = !!contratoPadre;
                    const saldoAnterior = hasMatch ? (contratoPadre.artDeudaGlobal || 0) : 0;
                    
                    // Calculamos la diferencia neta que realmente se debe inyectar
                    const ajusteNeto = totalDeudaExcel - saldoAnterior;
                    const hasAmount = totalDeudaExcel >= 0;
                    const hasVariation = Math.abs(ajusteNeto) > 0.01; // Evitamos desvíos de coma flotante
                    const isCorrect = hasMatch && hasAmount && hasVariation;

                    if (isCorrect) validos++;
                    else erroneos++;

                    // Determinación del tipo de movimiento según el sentido del ajuste
                    let calculatedTipoMovimiento = 'Saldo Inicial';
                    if (contratoPadre) {
                        if (ajusteNeto > 0) {
                            const hasHistory = historyMap[contratoPadre.id] || false;
                            calculatedTipoMovimiento = hasHistory ? 'Aumento de Deuda' : 'Saldo Inicial';
                        } else if (ajusteNeto < 0) {
                            calculatedTipoMovimiento = 'Disminución de Deuda';
                        }
                    }

                    // El importe del movimiento en el libro diario es el valor absoluto de la variación
                    const transaccionImporte = Math.abs(ajusteNeto);

                    let statusText = '';
                    if (!hasMatch) {
                        statusText = 'Contrato no encontrado';
                    } else if (!hasAmount) {
                        statusText = 'Importe debe ser >= 0';
                    } else if (!hasVariation) {
                        statusText = 'Saldo sin variaciones (Al día)';
                    } else {
                        statusText = `Listo (${calculatedTipoMovimiento})`;
                    }

                    rowsProcessed.push({
                        nContrato: nContratoExcel,
                        importe: transaccionImporte, // Guardamos la diferencia para el hito contable
                        importeTotalTarget: totalDeudaExcel, // Guardamos el saldo objetivo final
                        saldoAnterior,
                        contratoPadre: contratoPadre || null,
                        tipoMovimiento: calculatedTipoMovimiento,
                        fechaMovimiento: new Date().toISOString().substring(0, 10), // Fecha de Operación (Hoy)
                        estadoContrato: 'Sin Definir',
                        estadoGestion: 'Sin Asignar',
                        estadoDeuda: totalDeudaExcel > 0 ? 'Con Deuda' : 'Al día',
                        isCorrect,
                        statusText
                    });
                });

                setPreviewRows(rowsProcessed);
                setStats({ success: validos, errors: erroneos });
                setShowPreview(true);
                addLog(`Indexación terminada. ${validos} deudas listas para confirmación.`, 'info');

            } catch (fileError) {
                addLog(`Error de lectura: ${fileError.message}`, 'error');
            } finally {
                setProcessing(false);
            }
        };
        reader.readAsBinaryString(file);
    };

    /* STREAMING_CHUNK: Confirmando la inyección masiva de deudas en Firestore */
    /**
     * handleConfirmImport - Ejecuta la transacción de inserción en lote y actualiza contratos principales
     */
    const handleConfirmImport = async () => {
        if (previewRows.length === 0 || processing) return;
        setProcessing(true);
        addLog("Iniciando inyección masiva en cuentas corrientes de deudores...", "info");

        try {
            const batchId = new Date().getTime().toString();
            // Ruta corregida: public/data/remesas_deuda_art
            const remesaRef = doc(collection(db, 'public/data/remesas_deuda_art'));
            const remesaDocId = remesaRef.id;

            let creadosCount = 0;
            const creadosPaths = [];

            for (const item of previewRows) {
                if (!item.isCorrect) continue;

                try {
                    const contratoPadre = item.contratoPadre;
                    
                    const saldoAnterior = item.saldoAnterior;
                    const saldoResultante = item.importeTotalTarget; // Forzamos al total de la compañía

                    const parentPath = contratoPadre.path; 
                    const historialCollRef = collection(db, `${parentPath}/historial_cobranza`);
                    const newMovimientoRef = doc(historialCollRef);

                    // 1. Crear el hito de auditoría en la cuenta corriente por el monto de la diferencia
                    const payloadMovimiento = {
                        id: newMovimientoRef.id,
                        tipoMovimiento: item.tipoMovimiento,
                        importe: item.importe, // La diferencia neta aplicada
                        saldoAnterior: saldoAnterior,
                        saldoResultante: saldoResultante,
                        estadoContrato: item.estadoContrato, // "Sin Definir"
                        estadoGestion: item.estadoGestion,   // "Sin Asignar"
                        estadoDeuda: item.estadoDeuda,       
                        fechaMovimiento: item.fechaMovimiento, // Hoy
                        fechaCarga: serverTimestamp(),
                        batchId: batchId,
                        remesaDocId: remesaDocId,
                        comentario: `[IMPORTACIÓN MASIVA] Ajuste de balance comercial según reporte consolidado de compañía ($ ${saldoResultante.toLocaleString('es-AR', { minimumFractionDigits: 2 })}). Ajuste neto aplicado: $ ${item.importe.toLocaleString('es-AR', { minimumFractionDigits: 2 })}.`
                    };

                    await setDoc(newMovimientoRef, payloadMovimiento);

                    // 2. Actualizar el contrato principal con su nuevo saldo y estados
                    const contractRef = doc(db, parentPath);
                    await setDoc(contractRef, {
                        artDeudaGlobal: saldoResultante,
                        tieneDeuda: saldoResultante > 0,
                        artEstadoContrato: item.estadoContrato, // "Sin Definir"
                        artEstadoGestion: item.estadoGestion,   // "Sin Asignar"
                        artEstadoDeuda: item.estadoDeuda,       
                        artUltimoMovimientoTipo: item.tipoMovimiento,
                        artUltimoMovimientoMonto: item.importe,
                        artUltimoMovimientoFecha: item.fechaMovimiento,
                        artUltimoMovimientoResponsable: '',
                        artUltimaActualizacion: serverTimestamp()
                    }, { merge: true });

                    creadosPaths.push(newMovimientoRef.path);
                    creadosCount++;

                } catch (rowError) {
                    addLog(`Error al imputar deuda en contrato #${item.nContrato}: ${rowError.message}`, "error");
                }
            }

            if (creadosCount > 0) {
                // 3. Registrar remesa para control y eventual rollback
                // Ajustado: Se quitó 'compania' y 'usuarioNombre'
                await setDoc(remesaRef, {
                    id: remesaDocId,
                    fecha: new Date().toISOString(),
                    archivo: file.name,
                    registros: creadosCount,
                    batchId: batchId,
                    documentosCreados: creadosPaths
                });

                addLog(`[SISTEMA] Se imputaron ${creadosCount} deudas de ART. Remesa registrada correctamente.`, 'success');
                
                // NOTIFICAR ÉXITO AL OPERADOR
                setNotification({
                    show: true,
                    title: 'Importación Completada',
                    message: `Se han inyectado de forma masiva ${creadosCount} deudas en las cuentas corrientes de ART con total éxito. El lote de remesas ha sido registrado.`,
                    type: 'success'
                });
            } else {
                addLog("No se pudo imputar ningún registro en las cuentas corrientes.", "error");
                setNotification({
                    show: true,
                    title: 'Fallo de Importación',
                    message: "No se pudo persistir ningún registro en las cuentas corrientes. Por favor verifique el formato de su archivo.",
                    type: 'error'
                });
            }

            setFile(null);
            setPreviewRows([]);
            setShowPreview(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
            setContratosList([]); 
            setPrevMovementsMap({});

        } catch (error) {
            console.error("Error crítico durante la importación masiva de deudas:", error);
            addLog(`Error crítico de persistencia: ${error.message}`, 'error');
            setNotification({
                show: true,
                title: 'Error Crítico',
                message: `No se pudo completar la operación en lote: ${error.message}`,
                type: 'error'
            });
        } finally {
            setProcessing(false);
        }
    };

    /* STREAMING_CHUNK: Ejecutando el rollback de remesas con borrado de bitácoras y purga de raíz */
    /**
     * handleDeleteRemesa - Realiza el rollback borrando hitos y recalculando el estado de cada contrato
     */
    const handleDeleteRemesa = async () => {
        if (!remesaToDelete) return;
        setProcessing(true);
        try {
            addLog(`Iniciando remoción y re-auditoría contable delegada del lote...`, 'warning');

            // LLAMADA A LA FUNCIÓN DE BORRADO PROFUNDO CENTRALIZADA AUTÓNOMA
            await deleteRemesaDeudaArtFull(remesaToDelete.id);

            addLog(`Reversión de remesa "${remesaToDelete.archivo}" completada y balances restaurados con éxito.`, 'success');
            
            setNotification({
                show: true,
                title: 'Remesa Revertida',
                message: `La remesa de deudas "${remesaToDelete.archivo}" fue eliminada de forma masiva y todos los balances contables de los contratos afectados fueron recalculados a su estado histórico original.`,
                type: 'success'
            });
            
            setRemesaToDelete(null);
            fetchRemesas();
        } catch (error) {
            console.error("Error al revertir la remesa de deudas:", error);
            addLog(`Error crítico de rollback: ${error.message}`, 'error');
            setNotification({
                show: true,
                title: 'Error de Reversión',
                message: `Ocurrió un problema crítico al intentar realizar el rollback de la remesa: ${error.message}`,
                type: 'error'
            });
        } finally {
            setProcessing(false);
        }
    };

    const totalPages = Math.ceil(remesas.length / itemsPerPage);
    const paginatedRemesas = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return remesas.slice(start, start + itemsPerPage);
    }, [remesas, currentPage]);

    /* STREAMING_CHUNK: Renderizando la interfaz interactiva de deudas de ART y pestañas de control */
    return (
        <div className="min-h-screen bg-slate-50 p-8 md:p-12 font-sans text-slate-800 text-left transition-all duration-300 animate-fade-in">
            <div className="max-w-[95%] mx-auto">
                
                {/* CABECERA */}
                <div className="flex justify-between items-center mb-8 text-left select-none">
                    <div className="text-left">
                        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight text-left uppercase">Importador de Saldos ART</h1>
                        <p className="text-slate-500 mt-1.5 text-sm text-left font-medium">Carga masiva de deudas con validación de saldos y auditoría histórica.</p>
                    </div>
                    <button onClick={() => navigate('/cpanel')} className="px-5 py-2.5 text-slate-700 bg-white border border-slate-200 rounded-xl font-bold hover:text-red-600 shadow-sm transition-all uppercase text-[10px] tracking-widest active:scale-95 transform">Volver al Cpanel</button>
                </div>

                {/* TABS DE CONTROL */}
                <div className="flex space-x-1 bg-slate-200 p-1 rounded-xl w-fit mb-6 text-left select-none">
                    <button onClick={() => setActiveTab('importar')} className={`px-6 py-2 rounded-lg text-xs font-black uppercase transition-all ${activeTab === 'importar' ? 'bg-white text-red-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>Importar Saldos</button>
                    <button onClick={() => setActiveTab('remesas')} className={`px-6 py-2 rounded-lg text-xs font-black uppercase transition-all ${activeTab === 'remesas' ? 'bg-white text-red-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>Historial Remesas</button>
                </div>

                {/* VISTA: IMPORTADOR */}
                {activeTab === 'importar' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 text-left">
                        
                        <div className="lg:col-span-1 space-y-6 text-left">
                            {!showPreview ? (
                                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 text-left select-none">
                                    <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-3 text-left">Archivo Excel (.xlsx, .xls)</label>
                                    <div className="relative border-2 border-dashed border-slate-200 rounded-2xl p-8 hover:bg-slate-50 transition cursor-pointer flex flex-col items-center justify-center">
                                        <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".xlsx, .xls" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" disabled={processing} />
                                        <svg className="w-8 h-8 text-slate-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                                        <p className="text-sm text-slate-500 font-bold tracking-tight text-center">{file ? file.name : "Clic o arrastrar archivo"}</p>
                                    </div>
                                    <button onClick={parseExcelToPreview} disabled={!file || processing || loadingCaches} className={`w-full py-4 mt-6 rounded-xl font-black text-white shadow-lg flex items-center justify-center gap-2 uppercase text-xs tracking-widest transition-all ${!file || processing || loadingCaches ? 'bg-slate-300 shadow-none cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 shadow-red-100 active:scale-95 transform'}`}>
                                        {loadingCaches ? 'Auditando Contratos...' : processing ? 'Indexando...' : 'Previsualizar Lote'}
                                    </button>
                                </div>
                            ) : (
                                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 text-left space-y-4">
                                    <h3 className="text-sm font-black text-slate-700 uppercase tracking-wider border-b pb-2 select-none">Confirmar Importación</h3>
                                    <div className="flex gap-2">
                                        <button onClick={handleCancelPreview} className="flex-1 py-3.5 border border-slate-300 text-slate-600 font-bold uppercase text-xs tracking-wider rounded-xl hover:bg-slate-50 transition active:scale-95 transform select-none">
                                            Cancelar
                                        </button>
                                        <button onClick={handleConfirmImport} disabled={stats.success === 0 || processing} className="flex-2 py-3.5 px-6 bg-red-600 text-white font-black uppercase text-xs tracking-wider rounded-xl hover:bg-red-700 shadow-lg shadow-red-100 active:scale-95 transform select-none disabled:opacity-50 disabled:cursor-not-allowed">
                                            {processing ? 'Guardando...' : 'Confirmar Lote'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-3 text-center font-bold select-none animate-fade-in">
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100 text-emerald-600">
                                    <div className="text-lg font-black">{stats.success}</div>
                                    <div className="text-[8px] uppercase text-center font-black tracking-widest mt-1">Saldos Listos</div>
                                </div>
                                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100 text-rose-600">
                                    <div className="text-lg font-black">{stats.errors}</div>
                                    <div className="text-[8px] uppercase text-center font-black tracking-widest mt-1">Con Errores</div>
                                </div>
                            </div>
                        </div>

                        {/* SECCIÓN CONSOLA O TABLA PREVIA */}
                        <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden flex flex-col h-[600px] text-left relative">
                            {!showPreview ? (
                                <div className="p-6 flex flex-col h-full bg-slate-900 border border-slate-800 font-mono text-xs relative overflow-hidden text-left">
                                    <p className="text-slate-500 font-bold uppercase tracking-widest mb-4 border-b border-slate-800 pb-2 text-[10px] select-none">Consola de matching contable</p>
                                    <div className="flex-grow overflow-y-auto space-y-1.5 pr-2 custom-scrollbar text-left font-semibold">
                                        {loadingCaches && (
                                            <div className="text-amber-400 animate-pulse text-left">[SISTEMA] Leyendo deudas y auditando transacciones históricas en Firestore...</div>
                                        )}
                                        {logs.length === 0 && !loadingCaches && (
                                            <div className="text-slate-500 italic">Consola inactiva. Cargue un archivo Excel para verificar el estado de los balances de ART.</div>
                                        )}
                                        {logs.map((l, i) => (
                                            <div key={i} className={`flex gap-3 text-left ${l.type === 'error' ? 'text-rose-400 font-bold' : l.type === 'warning' ? 'text-amber-400' : l.type === 'success' ? 'text-emerald-400 font-bold' : 'text-slate-300'}`}>
                                                <span className="opacity-40 tabular-nums">[{l.time}]</span><span>{l.msg}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col h-full animate-fade-in text-left">
                                    <div className="p-4 bg-slate-50 border-b select-none flex justify-between items-center shrink-0">
                                        <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest">Previsualización de saldos entrantes</h4>
                                        <span className="text-[10px] font-black text-red-600 bg-red-50 px-2 py-0.5 rounded border border-red-100 uppercase">Imputaciones Directas</span>
                                    </div>
                                    <div className="flex-grow overflow-auto scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                                        <table className="w-full text-left text-xs border-collapse">
                                            <thead className="bg-slate-50/50 border-b text-slate-500 uppercase font-black text-[9px] tracking-wider select-none sticky top-0 bg-white z-10">
                                                <tr>
                                                    <th className="px-4 py-3">Contrato N°</th>
                                                    <th className="px-4 py-3">Asegurado</th>
                                                    <th className="px-4 py-3 text-right">Saldo Previo / Entrante</th>
                                                    <th className="px-4 py-3 text-center">Tipo Movimiento</th>
                                                    <th className="px-4 py-3 text-center">Estado</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {previewRows.map((row, idx) => (
                                                    <tr key={idx} className={`hover:bg-slate-50/50 ${!row.isCorrect ? 'bg-rose-50/30' : ''}`}>
                                                        <td className="px-4 py-3 font-bold text-slate-800">#{row.nContrato}</td>
                                                        <td className="px-4 py-3">
                                                            {row.contratoPadre ? (
                                                                <div className="flex flex-col text-left">
                                                                    <span className="font-bold text-slate-700">{row.contratoPadre.clienteNombre} {row.contratoPadre.clienteApellido || ''}</span>
                                                                    <span className="text-[10px] text-slate-400 font-bold uppercase">{row.contratoPadre.compania}</span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-rose-500 font-bold italic select-none">Sin correspondencia</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-mono">
                                                            <div className="text-slate-400 font-semibold">Previo: $ {(row.saldoAnterior).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</div>
                                                            <div className="font-black text-blue-700">Objetivo: $ {row.importeTotalTarget.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</div>
                                                            {row.isCorrect && (
                                                                <div className={`text-[10px] font-bold ${row.tipoMovimiento === 'Disminución de Deuda' ? 'text-emerald-600' : 'text-red-500'}`}>
                                                                    Ajuste: {row.tipoMovimiento === 'Disminución de Deuda' ? '-' : '+'} $ {row.importe.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-center font-bold text-slate-600">
                                                            {row.contratoPadre ? (
                                                                <span className={row.tipoMovimiento === 'Saldo Inicial' ? 'text-indigo-600' : row.tipoMovimiento === 'Disminución de Deuda' ? 'text-emerald-600' : 'text-amber-600'}>
                                                                    {row.tipoMovimiento}
                                                                </span>
                                                            ) : '-'}
                                                        </td>
                                                        <td className="px-4 py-3 text-center select-none">
                                                            <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase border tracking-tight ${
                                                                row.isCorrect 
                                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                                                                    : 'bg-rose-50 text-rose-700 border-rose-100 font-black'
                                                            }`}>
                                                                {row.statusText}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* HISTORIAL REMESAS */}
                {activeTab === 'remesas' && (
                    <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden animate-fade-in flex flex-col justify-between min-h-[500px] text-left">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm border-collapse">
                                <thead className="bg-slate-50 text-slate-500 uppercase font-black text-[9px] tracking-widest border-b select-none">
                                    <tr>
                                        <th className="px-6 py-4">Fecha de Lote (Últimos 3 Meses)</th>
                                        <th className="px-6 py-4">Canal / Cía</th>
                                        <th className="px-6 py-4">Archivo Cargado</th>
                                        <th className="px-6 py-4 text-center">Registros Creados</th>
                                        <th className="px-6 py-4 text-center">Reversión</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {loadingRemesas ? (
                                        <tr><td colSpan="5" className="p-16 text-center text-slate-400 font-bold animate-pulse text-xs uppercase tracking-widest">Cargando histórico de remesas...</td></tr>
                                    ) : remesas.length === 0 ? (
                                        <tr><td colSpan="5" className="p-16 text-center text-slate-400 italic">No se encontraron lotes de deudas de ART en los últimos 3 meses.</td></tr>
                                    ) : (
                                        paginatedRemesas.map(r => (
                                            <tr key={r.id} className="hover:bg-red-50/20 transition-colors">
                                                <td className="px-6 py-4 font-mono text-xs text-slate-500">{new Date(r.fecha).toLocaleString('es-AR')}</td>
                                                <td className="px-6 py-4 font-bold text-slate-800">ART DEUDAS</td>
                                                <td className="px-6 py-4 text-slate-500 italic select-all">{r.archivo}</td>
                                                <td className="px-6 py-4 text-center font-black text-red-600 tabular-nums">{r.registros}</td>
                                                <td className="px-6 py-4 text-center">
                                                    <button onClick={() => setRemesaToDelete(r)} className="text-rose-500 hover:text-white border border-rose-100 hover:bg-rose-500 px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all select-none">Revertir Lote</button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* PAGINACIÓN HISTORIAL */}
                        {totalPages > 1 && !loadingRemesas && (
                            <div className="px-6 py-4 bg-slate-50 border-t flex items-center justify-between select-none shrink-0">
                                <div className="text-xs text-slate-500 font-semibold">
                                    Mostrando <span className="font-bold text-slate-700">{((currentPage - 1) * itemsPerPage) + 1}</span> a{' '}
                                    <span className="font-bold text-slate-700">{Math.min(currentPage * itemsPerPage, remesas.length)}</span> de{' '}
                                    <span className="font-bold text-slate-700">{remesas.length}</span> remesas
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                        className={`px-3 py-1.5 rounded-lg border text-xs font-black uppercase tracking-wider transition-all ${
                                            currentPage === 1
                                                ? 'bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed'
                                                : 'bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 border-slate-200 active:scale-95 transform'
                                        }`}
                                    >
                                        Anterior
                                    </button>
                                    
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <button
                                            key={page}
                                            type="button"
                                            onClick={() => setCurrentPage(page)}
                                            className={`w-8 h-8 rounded-lg border text-xs font-black transition-all ${
                                                currentPage === page
                                                    ? 'bg-red-600 text-white border-red-600 shadow-sm'
                                                    : 'bg-white text-slate-500 hover:bg-slate-100 border-slate-200'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    ))}

                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                        className={`px-3 py-1.5 rounded-lg border text-xs font-black uppercase tracking-wider transition-all ${
                                            currentPage === totalPages
                                                ? 'bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed'
                                                : 'bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 border-slate-200 active:scale-95 transform'
                                        }`}
                                    >
                                        Siguiente
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            {/* MODAL ROLLBACK */}
            <ModalConfirmacion 
                show={!!remesaToDelete} 
                onClose={() => setRemesaToDelete(null)} 
                onConfirm={handleDeleteRemesa} 
                title="Revertir Lote de Deudas" 
                message={`¿Está completamente seguro de eliminar la remesa "${remesaToDelete?.archivo}"? Se borrarán de forma masiva los ${remesaToDelete?.registros} movimientos de deuda y las cuentas corrientes de los contratos afectados serán recalculadas a sus valores y estados previos de forma automática.`} 
            />

            {/* MODAL NOTIFICACIÓN INTEGRADO SÍNCRONO */}
            {notification.show && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[2000] flex items-center justify-center p-4 animate-fade-in text-left">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 border border-slate-100 relative">
                        <div className="flex items-center gap-3.5 mb-4">
                            <div className={`p-2.5 rounded-xl border ${
                                notification.type === 'success' 
                                    ? 'bg-emerald-50 text-emerald-600 border-emerald-100' 
                                    : 'bg-rose-50 text-rose-600 border-rose-100'
                            }`}>
                                {notification.type === 'success' ? (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                ) : (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                )}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-800">{notification.title}</h3>
                                <p className="text-[10px] text-slate-400 font-black tracking-wider uppercase">{notification.type === 'success' ? 'Operación Exitosa' : 'Incidencia Detectada'}</p>
                            </div>
                        </div>
                        <p className="text-slate-650 text-xs leading-relaxed font-medium mb-6">{notification.message}</p>
                        <div className="flex justify-end">
                            <button 
                                onClick={() => setNotification({ show: false, title: '', message: '', type: 'success' })} 
                                className={`px-6 py-2.5 rounded-xl font-bold uppercase text-[10px] tracking-widest text-white shadow-md active:scale-95 transition-all ${
                                    notification.type === 'success' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'
                                }`}
                            >
                                Entendido
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            `}</style>
        </div>
    );
};

export default ImportarDeudasArtPage;